import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   API_SUCCESS,
   APIStatus,
   getLoadingStatus
} from '@flowwai-frontend/shared/api-common'
import { LoadingWrapper } from '@flowwai-frontend/shared/common-components'
import {
   getTemplateIdFromURL,
   isEnableOpenSearchForGlobalSearch,
   useCRMItemUIStore
} from '@flowwai-frontend/bps-officer-web/common/ui'

import { TemplateBaseDetailsType } from '@flowwai-frontend/bps-officer-web/common/data-access'
import { usePipelineStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { useGetGSFields } from '../../apis/getGSFields/useGetGSFields'
import GlobalSearchHeader from '../../components/GlobalSearchHeader/GlobalSearchHeader'
import GSFieldsSelection from '../../components/GSFieldsSelection/GSFieldsSelection'
import GSTemplatePicker from '../../components/GSTemplatePicker/GSTemplatePicker'
import { useGlobalSearchUIStore } from '../../hooks/stores/useGetGlobalSearchUIStore'
import { GSFieldType } from '../../types'
import GSErrorView from '../GlobalSearchContentController/components/GSErrorView'
import NoDataView from '../GlobalSearchContentController/components/GSNoDataView'
import GSResultsList from '../GlobalSearchContentController/components/GSResultsList'
import { SEARCH_RESULTS_LIMIT } from '../GlobalSearchContentController/constants'
import useSearchPipelineItemsAPIHookWrapper from '../../apis/searchPipelineItemsAPIHookWrapper/useSearchPipelineItemsAPIHookWrapper'

import {
   commonClassNameForLoadingFailureNoDataView,
   containerClassName,
   pipeClassName,
   pipelineAndFieldsSelectionContainerClassName
} from './styles'
import './styles.css'

interface Props {
   templates: TemplateBaseDetailsType[]
}

const GlobalSearchTemplateContentController = (
   props: Props
): React.ReactElement => {
   const { templates } = props

   const location = useLocation()
   const activeTemplateId = getTemplateIdFromURL(location.pathname)

   const [selectedTemplateId, setSelectedTemplateId] = useState(
      activeTemplateId ?? templates[0]?.templateId
   )
   const { onOpenTableCRMItemDetailsSidePane } = useCRMItemUIStore()

   const [fields, setFields] = useState<GSFieldType[]>([])
   const [searchQuery, setSearchQuery] = useState('')
   const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([])
   const [canSearchOnAllFields, setCanSearchOnAllFields] = useState(true)

   const templateIds = templates.map(template => template.templateId)

   const { theme } = useAppTheme()
   const { fieldSelectionPipeBgColor } = theme.globalSearch.searchModal

   const pipelineStore = usePipelineStore()
   const globalSearchUIStore = useGlobalSearchUIStore()
   const { closeGlobalSearchModal } = globalSearchUIStore

   const shouldRenderResults = searchQuery !== ''

   const getFieldsAPI = useGetGSFields()
   const getGSResultsAPI = useSearchPipelineItemsAPIHookWrapper(
      isEnableOpenSearchForGlobalSearch
   )

   const requestObjectForGS = (offset = 0) => ({
      searchQuery,
      pipelineItemTemplateId: selectedTemplateId,
      fieldIds: canSearchOnAllFields ? [] : selectedFieldIds,
      canSearchOnAllFields: canSearchOnAllFields,
      pagination: { limit: SEARCH_RESULTS_LIMIT, offset }
   })

   const getGlobalSearchResults = () => {
      getGSResultsAPI.triggerAPI({
         requestObj: requestObjectForGS()
      })
   }

   const fetchMoreResults = (offset: number) => {
      getGSResultsAPI.fetchMore({ requestObj: requestObjectForGS(offset) })
   }

   const getFields = () => {
      getFieldsAPI.triggerAPI({
         salesTemplateIds: templateIds
      })
   }

   useEffect(() => {
      if (getFieldsAPI.fields.length > 0) {
         setFields(getFieldsAPI.fields)
         setSelectedFieldIds(getFieldsAPI.fields.map(field => field.fieldId))
      } else {
         setFields([])
         setSelectedFieldIds([])
      }
   }, [JSON.stringify(getFieldsAPI.fields)])

   useEffect(() => {
      getFields()
   }, [selectedTemplateId])

   useEffect(() => {
      if (searchQuery === '') return
      getGlobalSearchResults()
   }, [searchQuery, selectedTemplateId, JSON.stringify(selectedFieldIds)])

   const renderFailureView = (): React.ReactElement => {
      const onRetry = () => {
         if (getFieldsAPI.apiError) {
            getFields()
         } else {
            getGlobalSearchResults()
         }
      }
      return <GSErrorView onRetry={onRetry} />
   }

   const onClickGSResultItem = (
      pipelineItemId: string,
      pipelineId?: string
   ) => {
      if (!pipelineId) return

      const pipelineItemType = pipelineStore.pipelines.find(
         pipeline => pipeline.pipelineItemTemplateId === selectedTemplateId
      )?.pipelineItemType

      if (!pipelineItemType) return

      onOpenTableCRMItemDetailsSidePane(
         pipelineItemId,
         pipelineItemType,
         pipelineId,
         undefined,
         true
      )
      closeGlobalSearchModal()
   }

   const renderSuccessView = (): React.ReactElement => {
      const shouldRenderNoDataView = !(getGSResultsAPI.searchResults.length > 0)

      if (shouldRenderNoDataView) return <NoDataView />
      return (
         <GSResultsList
            isFromTemplateContent={true}
            totalSearchResults={getGSResultsAPI.totalCount ?? 0}
            searchResults={getGSResultsAPI.searchResults}
            fetchMoreResults={fetchMoreResults}
            onClickGSResultItem={onClickGSResultItem}
         />
      )
   }

   const hasMoreResults =
      getGSResultsAPI.totalCount &&
      getGSResultsAPI.totalCount > getGSResultsAPI.searchResults.length

   const getAPIStatus = (): APIStatus =>
      hasMoreResults
         ? API_SUCCESS
         : getLoadingStatus(getFieldsAPI.apiStatus, getGSResultsAPI.apiStatus)

   const shouldShowFields = () => {
      const matchPattern = /^[0-9+ ]+$/
      return matchPattern.test(searchQuery)
   }

   const onChangeSelectedFields = (ids: string[]) => {
      setCanSearchOnAllFields(false)
      setSelectedFieldIds(ids)
   }
   const onChangeTemplateId = (templateId: string) => {
      setCanSearchOnAllFields(true)
      setSelectedTemplateId(templateId)
   }

   return (
      <div
         className={containerClassName}
         data-testid='officer-global-search-template-content'
      >
         <GlobalSearchHeader onChangeSearchQuery={setSearchQuery} />
         <div className={pipelineAndFieldsSelectionContainerClassName}>
            <GSTemplatePicker
               selectedTemplateId={selectedTemplateId}
               setSelectedTemplateId={onChangeTemplateId}
               templates={templates}
            />

            {shouldShowFields() ? (
               <>
                  <div
                     className={`${pipeClassName} ${fieldSelectionPipeBgColor}`}
                  />
                  <GSFieldsSelection
                     fields={fields}
                     selectedFieldIds={selectedFieldIds}
                     setSelectedFieldIds={onChangeSelectedFields}
                     apiStatus={getFieldsAPI.apiStatus}
                     apiError={getFieldsAPI.apiError}
                  />
               </>
            ) : null}
         </div>
         {shouldRenderResults ? (
            <LoadingWrapper
               apiError={getGSResultsAPI.apiError || getFieldsAPI.apiError}
               apiStatus={getAPIStatus()}
               containerClassName={commonClassNameForLoadingFailureNoDataView}
               renderFailureView={renderFailureView}
            >
               {renderSuccessView()}
            </LoadingWrapper>
         ) : null}
      </div>
   )
}

export default observer(GlobalSearchTemplateContentController)
